<!--
  CollectorLineDetailSheet — one line whole (collector-app P1-D, handoff spec
  Section 2.6; mockup LineDetail.dc.html).

  A sheet, not a route page: a photo browser opened from inside a sheet is the
  repo's existing pattern (SupplierSourcePostSheet), so nothing fights. The
  photos are a swipeable full-width strip (native scroll-snap, no Swiper
  dependency) with the «1 / 12» counter, the tier caption and a thumb rail;
  tapping the picture opens Framework7's photo browser (dark, standalone) for
  pinch-zoom. The browser is created on demand and destroyed on EVERY close.

  The line's money is the card's three figures (CollectorFigures: quantity ×
  price = amount, her deposit under it, amber cells on a collected line that
  differs from the order) and a reservation's amber block — collector-clarity.

  Reads the line live from the day (the page passes it), so a mark made from
  here shows at once. The two mark buttons sit in the fixed footer (always
  reachable however long the post is); «المتاح بدل كده» is a door into the
  report sheet — the page closes this sheet first (one sheet at a time).

  A definite height, never `height: auto` (memory f7-sheet-auto-height-kills-scroll).
-->
<template>
  <f7-sheet
    class="collector-surface c-detail-sheet"
    :opened="opened"
    backdrop
    close-by-backdrop-click
    @sheet:closed="onClosed"
  >
    <f7-page-content v-if="line">
      <div class="c-detail">
        <button
          type="button"
          class="c-reset c-detail__close"
          :aria-label="t('collector.detail.close')"
          @click="emit('update:opened', false)"
        >
          <svg width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.4"
            stroke-linecap="round"
            aria-hidden="true"
          ><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>

        <!-- ── photos ── -->
        <section v-if="photos.length" class="c-hero" :aria-label="t('collector.detail.photosAria')">
          <div ref="track" class="c-hero__track" @scroll.passive="onScroll">
            <div v-for="(photo, index) in photos" :key="`${photo.url}-${index}`" class="c-hero__slide">
              <button type="button" class="c-reset c-hero__tap" @click="openBrowser(index)">
                <img
                  :src="heroSrc(photo)"
                  :alt="captionText(photo)"
                  loading="lazy"
                  crossorigin="anonymous"
                  draggable="false"
                  @error="onHeroError(photo)"
                />
              </button>
            </div>
          </div>
          <!-- <bdi>: «1 / 12» keeps its order inside an RTL line (an LTR run, isolated) -->
          <span class="c-hero__count c-num"><bdi>{{ counter }}</bdi></span>
          <span class="c-hero__caption">{{ captionText(photos[current]) }}</span>
        </section>
        <div v-if="photos.length > 1" class="c-rail">
          <button
            v-for="(photo, index) in photos"
            :key="`rail-${photo.url}-${index}`"
            type="button"
            class="c-reset c-rail__item"
            :aria-label="t('collector.detail.thumbAria', { n: index + 1 })"
            @click="goTo(index)"
          >
            <collector-photo
              size="rail"
              :src="photoThumb(photo)"
              :current="index === current"
            ></collector-photo>
          </button>
        </div>

        <div class="c-detail__body">
          <div class="c-detail__head">
            <h2 class="c-detail__title">
              {{ line.productName }}
              <span v-if="line.variantName" class="c-detail__variant">({{ line.variantName }})</span>
            </h2>
            <div v-if="metaLine" class="c-detail__meta c-num">
              {{ metaLine }}
            </div>
            <!-- the card's three figures, with her deposit and a reservation -->
            <collector-figures
              class="c-detail__figures"
              :line="line"
              :foot="!line.reservedForName"
            ></collector-figures>
            <div v-if="line.reservedForName" class="c-detail__reserved c-num">
              {{ t('collector.card.reserved', { name: line.reservedForName, deposit: pounds(line.depositCents) }) }}
            </div>
          </div>

          <!-- her answer, whole (the two reports sit in the fixed footer) -->
          <collector-line-answer :line="line" part="text"></collector-line-answer>

          <div v-if="line.memberNote" class="c-bar c-bar--purple">
            <div class="c-bar__label">{{ t('collector.detail.wants') }}</div>
            <div class="c-bar__text">
              <template v-for="(segment, index) in noteParts" :key="index">
                <b v-if="segment.bold">{{ segment.text }}</b>
                <template v-else>{{ segment.text }}</template>
              </template>
            </div>
          </div>
          <div v-if="line.leaderBrief" class="c-bar c-bar--indigo">
            <div class="c-bar__label">{{ t('collector.detail.brief') }}</div>
            <div class="c-bar__text">{{ line.leaderBrief }}</div>
          </div>

          <div v-if="triesLines.length" class="c-detail-tries">
            <div class="c-detail-tries__title">{{ t('collector.detail.triesTitle') }}</div>
            <div v-for="(text, index) in triesLines" :key="index" class="c-detail-tries__line">
              {{ text }}
            </div>
          </div>

          <div v-if="line.cartonReady" class="c-carton">
            <svg width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.4"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            ><path d="M12 3l9 16H3z" /><path d="M12 10v4M12 17v.5" /></svg>
            <span>{{ t('collector.detail.cartonReady') }}</span>
          </div>

          <button
            v-if="canReport"
            type="button"
            class="c-reset c-door"
            @click="emit('report', line)"
          >
            {{ t('collector.card.doorFull') }}
          </button>

          <div v-if="line.postText" class="c-post-full">
            <div class="c-post-full__label">{{ t('collector.detail.post') }}</div>
            <div class="c-post-full__text">{{ line.postText }}</div>
            <a
              v-if="line.postUrl"
              class="external c-post-full__link"
              :href="line.postUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>{{ t('collector.detail.openPost') }}</span>
              <svg width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              ><path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" /></svg>
            </a>
          </div>
        </div>
      </div>
    </f7-page-content>

    <template #fixed>
      <div v-if="line && hasFooter" class="sheet-fixed-footer c-detail-footer">
        <div v-if="state === 'markable'" class="c-detail-footer__buttons">
          <button type="button" class="c-reset c-big c-big--ok" @click="mark('collected')">
            {{ t('collector.line.collected') }}
          </button>
          <button type="button" class="c-reset c-big c-big--no" @click="mark('out_of_stock')">
            {{ t('collector.line.oos') }}
          </button>
        </div>
        <collector-line-answer
          v-else-if="answer?.answered && answer.canReport"
          :line="line"
          part="buttons"
          @report="(target, type) => reportAnswer(target, type)"
        ></collector-line-answer>
        <collector-line-status
          v-else
          :line="line"
          @reopen="(target) => emit('reopen', target)"
        ></collector-line-status>
      </div>
    </template>
  </f7-sheet>
</template>

<script setup>
import { f7 } from 'framework7-vue';
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  answerView,
  colourWord,
  lineState,
  memberAnswerKey,
  noteSegments,
  openMarkDays,
  orderedPhotos,
  photoCaption,
  photoCounter,
  photoMedium,
  photoThumb,
  tryDay,
  tryOutcomeKey,
} from '@/collector/lineView.js';
import CollectorFigures from '@/components/collector/CollectorFigures.vue';
import CollectorLineAnswer from '@/components/collector/CollectorLineAnswer.vue';
import CollectorLineStatus from '@/components/collector/CollectorLineStatus.vue';
import CollectorPhoto from '@/components/collector/CollectorPhoto.vue';
import { useCurrency } from '@/composables/useCurrency.js';

const props = defineProps({
  opened: { type: Boolean, default: false },
  /** The line, live from the day document (null while no line is chosen). */
  line: { type: Object, default: null },
});
const emit = defineEmits(['update:opened', 'mark', 'reopen', 'report', 'report-answer']);

const { t } = useI18n();
const { formatAmount } = useCurrency();
const pounds = (cents) => formatAmount(cents, { trimZeros: true });

const state = computed(() => lineState(props.line));
/** Her answer to the live question: its words are in the body, a yes puts the two reports in the footer. */
const answer = computed(() => answerView(props.line));
/**
 * Nothing to tap or say on a locked (day not started) or moved line: no empty
 * bar — and none for an answer that is only a «لأ» / «استنى» (the body says it).
 */
const hasFooter = computed(
  () => state.value !== 'locked' && state.value !== 'moved' && !(answer.value?.answered && !answer.value.canReport),
);
const photos = computed(() => orderedPhotos(props.line));
const noteParts = computed(() => noteSegments(props.line?.memberNote, colourWord(props.line)));

/** The door to the shelf report: any line still without a mark. */
const canReport = computed(() => props.line?.outcome === 'pending' && !props.line?.leaderState?.verified);

const metaLine = computed(() => {
  const { line } = props;
  if (!line?.channelName) return line?.memberCode ? t('collector.line.code', { code: line.memberCode }) : '';
  return t('collector.detail.meta', { channel: line.channelName, code: line.memberCode ?? '—' });
});

// ── Earlier tries, the member's tip and an open out-of-stock mark ────────────

const triesLines = computed(() => {
  const { line } = props;
  const out = [];
  for (const attempt of Array.isArray(line?.tries) ? line.tries : []) {
    const day = tryDay(attempt.date);
    const dayText = day.key ? t(day.key) : day.date;
    const outcome = t(tryOutcomeKey(attempt.outcome));
    const head = attempt.shop
      ? t('collector.detail.tryLine', { day: dayText, shop: attempt.shop, outcome })
      : t('collector.detail.tryLineNoShop', { day: dayText, outcome });
    out.push(attempt.collectorNote ? `${head} — ${t('collector.detail.tryNote', { note: attempt.collectorNote })}` : head);

    const answerKey = memberAnswerKey(attempt.memberAnswer);
    if (answerKey) {
      const said = attempt.memberAnswer.text ?? attempt.memberAnswer.optionLabel ?? attempt.memberAnswer.note ?? null;
      out.push(said ? t('collector.tries.answerQuoted', { answer: t(answerKey), text: said }) : t(answerKey));
    }
  }
  if (line?.tip?.shop) {
    const tip = t('collector.detail.tip', { shop: line.tip.shop });
    out.push(
      line.tip.priceSeenCents != null
        ? `${tip} · ${t('collector.detail.tipPrice', { price: pounds(line.tip.priceSeenCents) })}`
        : tip,
    );
  }
  if (line?.openOosMark?.shop) {
    out.push(
      t('collector.detail.openMark', {
        shop: line.openOosMark.shop,
        days: daysWords(openMarkDays(line.openOosMark.since)),
      }),
    );
  }
  return out;
});

function daysWords(days) {
  if (days == null) return '';
  if (days === 0) return t('collector.count.daysSince.zero');
  const bucket = days === 1 ? 'one' : days === 2 ? 'two' : days <= 10 ? 'few' : 'many';
  return t(`collector.count.daysSince.${bucket}`, { n: days });
}

// ── The photo strip ───────────────────────────────────────────────────────────

const track = ref(null);
const current = ref(0);
const counter = computed(() => photoCounter(current.value, photos.value.length));

function captionText(photo) {
  if (!photo) return '';
  const caption = photoCaption(photo, props.line);
  return t(caption.key, caption.params ?? {});
}

/** Which slide is centred — by the scroll offset, whichever way the page reads. */
function onScroll() {
  const el = track.value;
  if (!el || !el.clientWidth) return;
  const index = Math.round(Math.abs(el.scrollLeft) / el.clientWidth);
  current.value = Math.min(photos.value.length - 1, Math.max(0, index));
}

/** `scrollIntoView` is direction-agnostic (scrollLeft is negative in RTL). */
function goTo(index) {
  current.value = index;
  const slide = track.value?.children?.[index];
  slide?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'center' });
}

// A new line, or the sheet opening again, starts on the first photo.
// A big slide whose 320 px version is not on the phone (only the FIRST photo's
// is downloaded in the morning) shows the thumbnail the card already uses —
// blurry, but there — instead of going blank with no signal.
const failedHero = ref({});
const heroSrc = (photo) => (failedHero.value[photo.url] ? photoThumb(photo) : photoMedium(photo));
function onHeroError(photo) {
  if (!failedHero.value[photo.url] && photoThumb(photo) !== photoMedium(photo)) {
    failedHero.value = { ...failedHero.value, [photo.url]: true };
  }
}

// Separate sources, compared one by one: a getter returning an array fired on
// every rebuild of the day (each offline retry) and threw him back to photo 1.
watch(
  [() => props.opened, () => props.line?.id],
  async () => {
    failedHero.value = {};
    current.value = 0;
    await nextTick();
    if (track.value) track.value.scrollLeft = 0;
  },
);

// ── Framework7's photo browser (pinch-zoom) ───────────────────────────────────

let browser = null;

function destroyBrowser() {
  if (browser) {
    browser.destroy();
    browser = null;
  }
}

function openBrowser(index) {
  try {
    destroyBrowser();
    browser = f7.photoBrowser.create({
      photos: photos.value.map((photo) => ({ url: photo.url ?? photo.thumbUrl, caption: captionText(photo) })),
      type: 'standalone',
      toolbar: false,
      theme: 'dark',
    });
    browser.open(index);
  } catch (error) {
    console.error('[CollectorLineDetailSheet] photo browser failed:', error);
  }
}

function mark(type) {
  emit('mark', props.line, type);
}

/** «اشتريته» / «مش لقيته» on her answer — a report, not a mark. */
function reportAnswer(line, type) {
  emit('report-answer', line, type);
}

// The sheet is a page-level singleton reused for every line: destroy the
// browser on EVERY close, not only on unmount.
function onClosed() {
  destroyBrowser();
  if (props.opened) emit('update:opened', false);
}

onUnmounted(destroyBrowser);
</script>

<style scoped>
.c-detail-sheet {
  height: 94%;
  background: var(--c-ground);
  --sheet-fixed-footer-h: 76px;
  --f7-page-content-extra-padding-bottom: var(--sheet-fixed-footer-h);
}

.c-detail {
  position: relative;
}

.c-detail__close {
  position: absolute;
  inset-block-start: 12px;
  inset-inline-start: 12px;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 999px;
  background: rgba(28, 24, 48, 0.6);
  color: #ffffff;
}

.c-hero {
  position: relative;
  background: #1c1830;
}

.c-hero__track {
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.c-hero__track::-webkit-scrollbar { display: none; }

.c-hero__slide {
  flex: 0 0 100%;
  aspect-ratio: 1 / 1;
  max-height: 46vh;
  scroll-snap-align: center;
  background: var(--c-lilac);
}

.c-hero__tap {
  display: block;
  width: 100%;
  height: 100%;
}

.c-hero__tap img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #1c1830;
}

.c-hero__count {
  position: absolute;
  inset-block-end: 12px;
  inset-inline-end: 12px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(28, 24, 48, 0.7);
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
}

.c-hero__caption {
  position: absolute;
  inset-block-end: 12px;
  inset-inline-start: 12px;
  max-width: 70%;
  padding: 4px 10px;
  border-radius: var(--c-radius-chip);
  background: rgba(49, 0, 111, 0.88);
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
}

.c-rail {
  display: flex;
  gap: 8px;
  padding: 10px 16px;
  overflow-x: auto;
  background: var(--c-card);
  border-bottom: 1px solid var(--c-border);
}

.c-rail__item {
  flex-shrink: 0;
  padding: 2px;
}

.c-detail__body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
}

.c-detail__title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
}

.c-detail__variant {
  font-weight: 500;
  color: var(--c-muted);
}

.c-detail__meta {
  margin-top: 2px;
  font-size: 13px;
  color: var(--c-muted);
}

/* the sheet is the ground itself: the plain figure cells are cards on it */
.c-detail__figures {
  --c-fig-plain: var(--c-card);
  margin-top: 10px;
}

.c-detail__reserved {
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: var(--c-radius-chip);
  background: var(--c-amber-fill);
  color: var(--c-amber-text);
  font-size: 13px;
  font-weight: 700;
}

.c-bar {
  padding: 10px 14px;
  border-radius: var(--c-radius-row);
  background: var(--c-card);
  border-inline-start: 4px solid var(--c-purple);
}

.c-bar--indigo { border-inline-start-color: var(--c-indigo); }

.c-bar__label {
  font-size: 12px;
  font-weight: 700;
  color: var(--c-muted);
}

.c-bar__text {
  margin-top: 2px;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-line;
}

.c-detail-tries {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 14px;
  border: 1px solid var(--c-amber-border);
  border-radius: var(--c-radius-row);
  background: var(--c-amber-fill);
}

.c-detail-tries__title { font-size: 13px; font-weight: 700; color: var(--c-amber-text); }
.c-detail-tries__line { font-size: 13px; color: var(--c-amber-ink); line-height: 1.5; }

.c-carton {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 14px;
  border: 1px solid var(--c-red-border);
  border-radius: var(--c-radius-row);
  background: var(--c-red-fill);
  color: var(--c-red-text);
  font-size: 13px;
  font-weight: 700;
  line-height: 1.5;
}

.c-carton svg { flex-shrink: 0; margin-top: 1px; }

.c-door {
  height: 44px;
  border: 1px solid var(--c-border);
  border-radius: var(--c-radius-chip);
  background: var(--c-card);
  color: var(--c-indigo);
  font-size: 14px;
  font-weight: 700;
  text-align: center;
}

.c-post-full {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  border-radius: var(--c-radius-row);
  background: var(--c-card);
}

.c-post-full__label { font-size: 12px; font-weight: 700; color: var(--c-muted); }
.c-post-full__text { font-size: 14px; line-height: 1.7; white-space: pre-line; }

.c-post-full__link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  color: var(--c-indigo);
  font-size: 14px;
  font-weight: 700;
}

.c-detail-footer__buttons {
  display: flex;
  gap: 10px;
}

.c-big {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 0;
  height: 52px;
  border-radius: var(--c-radius-button);
  font-size: 17px;
  font-weight: 700;
  box-sizing: border-box;
}

.c-big--ok { background: var(--c-green-text); color: #ffffff; }
.c-big--no { border: 2px solid var(--c-red-border); background: var(--c-card); color: var(--c-red-text); }
</style>
